import './button.scss';

import React, { forwardRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { asLink } from './anchor';
import Spinner from './spinner';

const Button = forwardRef((props, ref) => {
  const isMounted = useRef(false);
  const [isLoading, setIsLoading] = useState(props.isLoading);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      setIsLoading(props.isLoading);
    }
  }, [props.isLoading]);

  const disabled = isLoading ? true : props.disabled;

  const className = classNames({
    btn: true,
    'btn-block': props.block,
    [`btn-${props.type}`]: true,
    [`btn-${props.size}`]: props.size,
    [`disabled`]: disabled,
    [props.className]: props.className,
  });

  const handleOnClick = async e => {
    if (isLoading) {
      e.preventDefault();
      return;
    }

    setIsLoading(true);
    await props.onClick(e);
    if (isMounted.current) {
      setIsLoading(false);
    }
  };

  return (
    <button
      ref={ref}
      onClick={props.onClick ? handleOnClick : undefined}
      type={props.htmlType}
      className={className}
      disabled={disabled}
    >
      {isLoading ? (
        <>
          <Spinner size="sm" theme={null} /> {props.isLoadingLabel}
        </>
      ) : (
        props.children
      )}
    </button>
  );
});

Button.propTypes = {
  type: PropTypes.oneOf([
    'default',
    'primary',
    'secondary',
    'warning',
    'danger',
    'success',
    'outline',
    'link',
    'ancestry',
    'outline-primary',
    'facebook',
    'amazon',
  ]),
  block: PropTypes.bool,
  htmlType: PropTypes.oneOf(['button', 'submit', 'reset']),
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['xs', 'sm', 'lg']),
  isLoading: PropTypes.bool,
  isLoadingLabel: PropTypes.string,
  className: PropTypes.string,
  onClick: PropTypes.func,
  children: PropTypes.node,
};

Button.defaultProps = {
  type: 'default',
  block: false,
  htmlType: 'button',
  isLoading: false,
  isLoadingLabel: 'Loading ...',
  disabled: false,
};

export default asLink(Button);
